import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { LangContext } from '../../../contexts/langContext';
import { SocialContext } from '../../../contexts/socialContext';
import { StateContext } from '../../../contexts/stateContext';
import { Preloader, Preloader0 } from '../../UiKit/kit';
import { parseDesignInPersonas } from '../../utilsDesign';
import { fetchGeoUsers, withCityLocation } from '../../utilsGeo';
import { ListAvatars } from '../ListAvatars';
import { CelebsContext } from '../../../contexts/celebsContext';
import { CityMap } from '../CityMap';
import { City } from '../../UiKit/City';
import { IconList } from '../../UiKit/IconList';
import './PeopleMap.css';
import { Icon } from '../../UiKit/Icon';
import { MapClouds } from './MapClouds';
import { Button } from '../../UiKit/Button';

const CLOUD_PART_DELAY = 0;
const CLOUD_UNMOUNT_MS = 800;
const CLOUD_MAX_COVER_MS = 2000;

// The "look at this city" target is a view-only preference; keep it around so the
// map reopens where the user left it.
const FIND_CITY_STORAGE_KEY = 'peopleMapFindCity';

const readStoredFindCity = () => {
  try {
    const raw = localStorage.getItem(FIND_CITY_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.location ? parsed : null;
  } catch {
    return null;
  }
};

const getClusterTitle = (people = []) => {
  const first = people[0];
  if (!first) return '';
  const aggr = first.user?.aggr || first.aggr || first.avatar?.aggr;
  if (!aggr) return '';
  return (aggr.geo || aggr.city)?.city || '';
};

export const Map = React.memo(({
  filterPeople = (arr) => arr,
  filter
}) => {

  const context = {
    ...useContext(SocialContext),
    ...useContext(StateContext),
    ...useContext(LangContext),
    ...useContext(CelebsContext)
  };
  const $ui = context.$ui;
  const [cityId, setCityId] = useState(context.me ? context.me.user.city_id : null);
  const [cityObj, setCityObj] = useState(context.me ? context.myCity : {});

  const [findCity, setFindCityInner] = useState(() => readStoredFindCity());
  const findCityDefaultedRef = useRef(false);

  const [mapPeople, setMapPeople] = useState();
  const [clusterSelection, setClusterSelection] = useState(null);
  const [cloudsPhase, setCloudsPhase] = useState('covered');
  const cloudsPartedRef = useRef(false);

  const partClouds = useCallback(() => {
    if (cloudsPartedRef.current) return;
    cloudsPartedRef.current = true;
    setTimeout(() => setCloudsPhase('parting'), CLOUD_PART_DELAY);
    setTimeout(() => setCloudsPhase('gone'), CLOUD_PART_DELAY + CLOUD_UNMOUNT_MS);
  }, []);

  // Start the reveal as early as possible: the moment people data is ready the
  // map begins rendering underneath, so we don't wait for its onLoad event.
  useEffect(() => {
    if (mapPeople) partClouds();
  }, [mapPeople, partClouds]);

  // Safety net: never keep the clouds forever if the map never signals ready.
  useEffect(() => {
    const t = setTimeout(partClouds, CLOUD_MAX_COVER_MS);
    return () => clearTimeout(t);
  }, [partClouds]);

  const loadMapPeople = () => {
    fetchGeoUsers({}, (data) => {
      setMapPeople(parseDesignInPersonas((data || []).map(withCityLocation)));
    });
  };

  useEffect(() => {
    loadMapPeople();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!context.myCity?.id) return;
    setCityId(context.myCity.id);
    setCityObj(context.myCity);
  }, [context.myCity?.id]);

  const setFindCity = useCallback((city) => {
    setFindCityInner(city || null);
    try {
      if (city && city.location) {
        localStorage.setItem(FIND_CITY_STORAGE_KEY, JSON.stringify(city));
      } else {
        localStorage.removeItem(FIND_CITY_STORAGE_KEY);
      }
    } catch {
      // ignore storage failures (private mode, quota, etc.)
    }
  }, []);

  // By default, look at the user's own city — but only once, and never override a
  // stored/explicit choice.
  useEffect(() => {
    if (findCityDefaultedRef.current) return;
    if (findCity) {
      findCityDefaultedRef.current = true;
      return;
    }
    if (context.myCity?.id && context.myCity.location) {
      findCityDefaultedRef.current = true;
      setFindCity(context.myCity);
    }
  }, [context.myCity?.id, context.myCity, findCity, setFindCity]);


  const handleSaveCity = () => {
    if (cityId) {
      context.x.updateMyCity(cityId, () => loadMapPeople(), cityObj);
    }
  };


  const handleEmpty = () => {
    setCityId();
    setCityObj({});
  };

  const handleSelectCluster = (selection) => {
    setClusterSelection(selection);
  };

  const myCityId = context.myCity?.id || context.me?.user?.city_id || null;

  return (
    <>

      <div className={`people-map ${mapPeople ? ' people-map--loaded' : ''} ${clusterSelection ? ' people-map--cluster-open' : ''}`}>
        {!myCityId && <div className="x_map_mylocation__no-city"/>}
        
        {!clusterSelection && <div className={`x_map_mylocation ${!myCityId ? ' x_map_mylocation--no-city' : ''}`}>

            <div className={`x_choose_city ${!myCityId ? ' x_choose_city--no-city' : ''}`}>
              <City
                icon={!myCityId ? 'location_on' : 'home'}
                locateMe={true}
                placeholder={$ui('user_city_placeholder')}
                city={cityObj}
                show={['city', 'province', 'country']}
                className={'x_input--cityprofile'}
                value={cityId}
                handleCityId={(id) => { setCityId(id) }}
                handleCityObj={cityObj => {
                  context.x.addCityHistory(cityObj)
                  setCityObj(cityObj)
                  context.x.updateMyCity(cityObj.id, () => loadMapPeople(), cityObj);
                }}
                handleEmpty={handleEmpty}
                recents={true}
              />
            </div>

            {Boolean(cityId) && context.me && cityId !== context.me.user.city_id && <>
              {context.updatingAva !== context.me.user.token && <div className='x_choose_city_my'><div className="x_button x_button--icon x_button--setmycity"
                onClick={() => handleSaveCity()}><div className="t_icon">check</div>
                <span className="mobile_v_no">{$ui('process_save')}</span>
              </div></div>}
              {context.updatingAva === context.me.user.token && <div className="x_button x_button--inactive x_button--ghost x_button--icon"><div className="t_icon">check</div><span className="mobile_v_no">{$ui('process_saving')}</span></div>}
            </>}

        </div>}

        {!myCityId && <div className="x_map_mylocation__no-city__label">
          {$ui('user_city_placeholder_description')}
        </div>}

        {!clusterSelection && !!myCityId && <div className={`x_map_findlocation`}>
            <div className={`x_choose_city`}>
              <City
                icon={'search'}
                locateMe={false}
                placeholder={$ui('find_location_placeholder')}
                city={findCity || {}}
                show={['city', 'province', 'country']}
                className={'x_input--cityprofile'}
                value={findCity?.id || ''}
                handleCityId={() => { }}
                handleCityObj={cityObj => setFindCity(cityObj)}
                handleEmpty={() => setFindCity(null)}
                recents={false}
              />
            </div>

            {/* {Boolean(context.myCity?.id && context.myCity.location && context.myCity?.id !== findCity?.id) && (
              <Button
                className="x_button x_button--icon x_button--ghost x_map_findlocation_me"
                onClick={() => setFindCity(context.myCity)}
                icon="close"
              >
              </Button>
            )} */}

        </div>}


        {!mapPeople && (
          <div className="people-map__loading">
            <Preloader0 style={{ zIndex: 100, height: '100%', position: 'relative' }}>
              <Preloader width={80} height={80} /> {$ui('loading')}...
            </Preloader0>
          </div>
        )}



        {mapPeople && (
          <>
            <div className={`people-map__map${clusterSelection ? ' people-map__map--collapsed' : ''}`}>
              
              <CityMap
                people={mapPeople}
                me={context.me}
                myCity={context.myCity}
                focusCity={findCity}
                compact={Boolean(clusterSelection)}
                selectedCityIds={clusterSelection?.cityIds}
                selectionCoordinates={clusterSelection?.coordinates}
                onSelectCluster={handleSelectCluster}
                onReady={partClouds}
                onCenterMe={() => setFindCity(context.myCity)}
              />
              {clusterSelection && (
                <button
                  type="button"
                  className="people-map__map-overlay"
                  onClick={() => setClusterSelection(null)}
                  aria-label={$ui('close') || 'Close'}
                />
              )}
            </div>

            {clusterSelection && (
              <>
                {/* <div className="x_choose_section_title0 x_choose_section_title0--v people-map__title">
                  <div className="x_choose_section_header x_choose_section_header--v">
                    <div className="x_choose_section_title x_choose_section_title--v">
                      <IconList icon="travel_explore" q={clusterSelection.people.length} />
                      {getClusterTitle(filterPeople(clusterSelection.people, filter))}
                      <div className="x_choose_menu_counter">
                        {clusterSelection.people.length}
                      </div>
                      <div className="x_choose_section_header_grad0 r" />
                    </div>
                    <div className="x_choose_section_header_right">
                      <button
                        type="button"
                        className="people-map__grid-close"
                        onClick={() => setClusterSelection(null)}
                        title={$ui('close') || 'Close'}
                      >
                        <span className="t_icon">close</span>
                      </button>
                    </div>
                  </div>
                </div> */}

                <div className="people-map__grid">
                  <ListAvatars
                    listId="followings"
                    array={filterPeople(clusterSelection.people, filter)}
                    quantity={clusterSelection.people.length}
                    actions={['share', 'follow']}
                    fullSrceen={true}
                    mode="hd"
                    paginate={36}
                    celeb={true}
                  />
                </div>
              </>
            )}
          </>
        )}

        {cloudsPhase !== 'gone' && <MapClouds phase={cloudsPhase} />}

      </div>

    </>
  )
})

export default Map;
